import type { CSSProperties, ForwardedRef, HTMLAttributes } from 'react';
import { forwardRef } from 'react';

import cx from 'classnames';

import { CUSTOM_ICONS_FILE_NAME_SET } from '../customIconsFileNameSet';
import { DEPRECATED_IONIC_ICON_TO_REMIX_ICON_NAME_MAP } from '../ionicIconToRemixIconNameMap';
import type { IconSize } from '../types';
import { CustomIcon } from './CustomIcon';
import { iconSize } from './iconSize';

import styles from './GlossIcon.module.css';

type Props = HTMLAttributes<HTMLSpanElement> & {
  /**
   * The name of the icon. This can be a Remix icon name (e.g. `add-circle`), or a custom icon name (e.g. `gg-card-clock-line`).
   */
  icon: string;
  alt?: string;
  className?: string;
  /**
   * The size of the icon. Can be a number (pixels) or a IconSize string ( either `xs` | `sm` | `md` | `lg` | `xl`).
   */
  size?: IconSize | number;
};

const IconWrapperRenderFunction = (
  { icon, alt, size = 'md', className, ...props }: Props,
  ref: ForwardedRef<HTMLSpanElement>,
) => {
  const actualSize = typeof size === 'number' ? `${size}px` : iconSize[size];
  const iconName =
    icon in DEPRECATED_IONIC_ICON_TO_REMIX_ICON_NAME_MAP
      ? DEPRECATED_IONIC_ICON_TO_REMIX_ICON_NAME_MAP[icon]
      : icon;
  const isCustomIcon = CUSTOM_ICONS_FILE_NAME_SET.has(iconName);

  return (
    <span
      ref={ref}
      className={cx(styles.root, className)}
      style={
        {
          '--glossicon-size': actualSize,
        } as CSSProperties
      }
      {...props}
    >
      {icon && (
        <>
          {isCustomIcon ? (
            <CustomIcon iconFileName={iconName} />
          ) : (
            <i className={`ri-${iconName}`} />
          )}
        </>
      )}

      {alt && <span className="sr-only">{alt}</span>}
    </span>
  );
};

/**
 * The GlossIcon component is a wrapper around Remix, Ionic (soon to be deprecated) and our own gg custom icons.
 * If your icon is not visible - ensure that you are using the right icon name.
 *
 * For remix icons you can visit: https://remixicon.com/. to find the icon name.
 *
 * For custom icons, ensure you add them to `glossDS/Icons/svgs/` directory
 * and run `node src/glossDS/tokens/utils/iconsDirectoryParser.js` script to generate the icon names.
 */
export const GlossIcon = forwardRef(IconWrapperRenderFunction);
